import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import dayjs from "dayjs";

// mui
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";

import InputAdornment from "@mui/material/InputAdornment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ButtonBase from "@mui/material/ButtonBase";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// lib
import formatMoney from "lib/formatMoney";
import scroller from "lib/scroller";
import sleep from "lib/sleep";
// components
import handleUserSelection from "components/Calculators/components/utils/handleUserSelection";
import CalculatorQuestions from "components/Calculators/CashOnCashReturnCalculator/components/CalculatorQuestions";
import loadCalculation from "components/Calculators/components/utils/loadCalculation";
import createCalc from "components/Calculators/DSCRCalculator/utils/createCalc";
import updateCalc from "components/Calculators/DSCRCalculator/utils/updateCalc";
import * as gtag from "components/GTagManager";
import dynamic from "next/dynamic";
const AddressAutocomplete = dynamic(() =>
  import("components/AddressAutocomplete")
);
import SaveSharePDF from "components/Calculators/components/SaveSharePDF";
import QuestionRenderer from "components/Calculators/components/QuestionRenderer";
import Authenticator from "components/Authenticator";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

const calcType = "Cash on cash return";

export default function CashOnCashReturnCalculator({
  externalCalc,
  loadCalcs,
  refCalcs,
  externalExampleCalc,
}) {
  const theme = useTheme();
  const router = useRouter();

  const [suggestionChoice, setSuggestionChoice] = useState(false);
  const [HUDSummary, setHUDSummary] = useState({});
  const {
    user,
    userProfile,
    isNewUser,
    setSnackbarData,
    showLogInDialog,
    setShowLogInDialog,
  } = useStore(AppStore);
  const [loanQuoteData, setLoanQuoteData] = useState({});
  const [resultsData, setResultsData] = useState([]);
  const [singleItemAnalysisData, setSingleItemAnalysisData] = useState({});

  const [showQuestionDescription, setShowQuestionDescription] = useState({
    "Monthly cashflow": true,
    "Annual cashflow": true,
    "Invested cash": true,
    "Cash on cash return": true,
  });

  // Answers is what users give and shown in the UI
  const [answers, setAnswers] = useState([]);
  // Questions is what has blueprint of what we ask
  const [questions, setQuestions] = useState([]);
  // All current session answers are held in this
  const [currentValues, setCurrentValues] = useState({});
  const [loanAmount, setLoanAmount] = useState({});
  const [loanPayment, setLoanPayment] = useState(0);

  const [totalMonthlyExpenses, setTotalMonthlyExpenses] = useState(0);
  const [monthlyCashflow, setMonthlyCashflow] = useState(0);
  const [annualCashflow, setAnnualCashflow] = useState(0);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [cashOnCashReturn, setCashOnCashReturn] = useState(0);

  const [showSaveFields, setShowSaveFields] = useState(false);
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [calc, setCalc] = useState(externalCalc ? externalCalc : null);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  const [showMeMyRate, setShowMeMyRate] = useState(false);

  const [anchorEl, setAnchorEl] = useState({});
  // Add this with other state variables at the top
  const [totalPayment, setTotalPayment] = useState(0);
  const [basePayment, setBasePayment] = useState(0);

  // special section that isn't part of original questions
  // but must be added later so we can still capture in 'answers'
  const firstPartOfQuestions = [
    "Monthly rent",
    "Monthly mortgage",
    "Annual taxes",
    "Annual insurance",
    "Monthly reserve",
  ];

  const secondPartOfQuestions = ["Downpayment", "Closing costs"];

  const thirdPart = ["Calculation name", "Address (optional)"];

  // Add this state at the top with other state declarations
  const [showFullSchedule, setShowFullSchedule] = useState(false);

  const handleOpen = (event, calc) => {
    const updatedAnchorEl = Object.assign({}, anchorEl);
    updatedAnchorEl[calc.id] = event.currentTarget;
    setAnchorEl(updatedAnchorEl);
  };

  const handleClose = (calc) => {
    if (calc) {
      const updatedAnchorEl = Object.assign({}, anchorEl);
      delete updatedAnchorEl[calc.id];
      setAnchorEl(updatedAnchorEl);
    }
  };

  // Custom hook to get the previous state
  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  // Save the previous state
  const previousValues = usePrevious(currentValues);

  useEffect(() => {
    const defaultValues = {};

    loadCalculation(
      defaultValues,
      externalCalc,
      externalExampleCalc,
      CalculatorQuestions,
      setQuestions,
      setAnswers,
      setCurrentValues,
      setShowUpdateFields
    );
  }, []);

  useEffect(() => {
    if (Object.keys(currentValues).length > 0) {
      // Get values from inputs
      // First part values

      const monthlyRent = parseFloat(currentValues["Monthly rent"]) || 0;
      const monthlyMortgage =
        parseFloat(currentValues["Monthly mortgage"]) || 0;
      const annualTaxes = parseFloat(currentValues["Annual taxes"]) || 0;
      const annualInsurance =
        parseFloat(currentValues["Annual insurance"]) || 0;
      const monthlyReserve = parseFloat(currentValues["Monthly reserve"]) || 0;

      const downpayment = parseFloat(currentValues["Downpayment"]) || 0;
      const closingCosts = parseFloat(currentValues["Closing costs"]) || 0;

      // Calculate monthly cashflow
      const monthlyTaxes = annualTaxes / 12;
      const monthlyInsurance = annualInsurance / 12;

      // Calculate total monthly expenses
      const totalMonthlyExpenses =
        monthlyMortgage + monthlyTaxes + monthlyInsurance + monthlyReserve;

      // Calculate monthly cashflow (income - expenses)
      const monthlyCashflow = monthlyRent - totalMonthlyExpenses;

      // Calculate annual cashflow
      const annualCashflow = monthlyCashflow * 12;

      // Calculate total investment
      const totalInvestment = downpayment + closingCosts;

      // Calculate cash on cash return (if investment is greater than 0)
      const cashOnCashReturn =
        totalInvestment > 0 ? annualCashflow / totalInvestment : 0;

      // Update state with calculated values

      setTotalMonthlyExpenses(totalMonthlyExpenses);
      setMonthlyCashflow(monthlyCashflow);
      setAnnualCashflow(annualCashflow);
      setTotalInvestment(totalInvestment);
      setCashOnCashReturn(cashOnCashReturn);
    }
  }, [currentValues]);

  const generateAnswerOptions = (answer, index, answers) => {
    let answerOptionsUI;

    switch (answer.a_type) {
      case "leaf":
        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`} sx={{ mb: 2 }}>
            <div></div>
          </Box>
        );
        break;
      case "percent":
        let localValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : !isNaN(currentValues[answer.q_title])
            ? currentValues[answer.q_title]
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              margin: theme.spacing(1),
              width: "100%",
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                width: "100%",
                [theme.breakpoints.down("md")]: {
                  padding: `0 !important`,
                },
              }}
              error={answer.a_error}
              InputLabelProps={{ shrink: localValue }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_input_type}
              value={localValue || ""}
              onChange={(e) => {
                const num = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;

                setCurrentValues(updatedCurrentValues);

                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
          </Box>
        );
        break;
      case "mc":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              flex: "0 1 100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {answer.a_opts(answers).map((a_opt, index) => {
              {
                return (
                  <Button
                    id={
                      index === 0
                        ? `text_field_${answer.q_title}_${
                            calc?.id ? calc.id : 0
                          }`
                        : `text_field_${answer.q_title}_opt_${index}`
                    }
                    sx={{ margin: theme.spacing(1) }}
                    variant={
                      currentValues?.[answer.q_title] === a_opt
                        ? "contained"
                        : "outlined"
                    }
                    onClick={(e) => {
                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = a_opt;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        a_opt,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const updatedCurrentValues = Object.assign(
                          {},
                          currentValues
                        );
                        updatedCurrentValues[answer.q_title] = a_opt;

                        setCurrentValues(updatedCurrentValues);

                        handleUserSelection(
                          a_opt,
                          answer,
                          answers,
                          questions,
                          setAnswers
                        );

                        const next_q_id = answer.q_next_step_func(
                          answer,
                          answers,
                          questions
                        );

                        if (next_q_id) {
                          const nextQuestion = questions.find(
                            (q) => q.q_id === next_q_id
                          );

                          const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                          const nextQuestionTextField = document.getElementById(
                            nextQuestionTextFieldId
                          );

                          if (nextQuestionTextField) {
                            nextQuestionTextField.focus();
                          }
                        }
                      }
                    }}
                  >
                    {a_opt}
                  </Button>
                );
              }
            })}
          </Box>
        );
        break;
      case "text":
        const localTextValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : currentValues[answer.q_title];

        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`}>
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{ margin: theme.spacing(5, 0) }}
              error={answer.a_error}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_input_type}
              value={localTextValue}
              InputLabelProps={{ shrink: localTextValue }}
              onChange={(e) => {
                const val = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = val;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  val,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
            />
          </Box>
        );
        break;
      case "currency":
        const uiValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string && answer.a_string.length > 0
              ? `$${formatMoney(answer.a_string, 0)}`
              : null
            : !isNaN(currentValues[answer.q_title]) &&
              currentValues[answer.q_title]
            ? `$${formatMoney(currentValues[answer.q_title], 0)}`
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(3),
              width: "100%",
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={answer.q_help_text ? answer.q_help_text : ""}
              sx={{ width: "100%" }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              inputProps={{
                inputMode: "numeric", // This makes mobile to pull up numbers only keyboard
              }}
              value={uiValue || ""}
              onChange={(e) => {
                const num = e.target.value.replace(/\D/g, "");

                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
          </Box>
        );
        break;
      case "number":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
              width: "100%",
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text
                  : answer.q_help_text
                  ? answer.q_help_text
                  : ""
              }
              error={answer.a_error}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              value={
                currentValues[answer.q_title] === undefined
                  ? answer.a_string
                    ? answer.a_string
                    : null
                  : currentValues[answer.q_title]
              }
              InputLabelProps={{
                shrink:
                  currentValues[answer.q_title] === undefined
                    ? answer.a_string
                      ? answer.a_string
                      : null
                    : currentValues[answer.q_title],
              }}
              onChange={(e) => {
                const value = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = value;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  value,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );

                // this is done to reset
                // 'get rate' output when new stuff is typed in
                setLoanQuoteData({});
              }}
            />
          </Box>
        );
        break;
      case "address":
        const address = calc?.Address;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
            sx={{ margin: theme.spacing(3, 0, 3, 0) }}
          >
            <AddressAutocomplete
              autocompleteOnKeyDown={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                if (e.key === "Enter") {
                  setSuggestionChoice(place.formatted_address);

                  handleUserSelection(
                    place.formatted_address,
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );
                }
              }}
              autocompleteOnClick={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                setSuggestionChoice(place.formatted_address);

                handleUserSelection(
                  place.formatted_address,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              textFieldLabel={answer.a_error ? "Error" : answer.q_title}
              textFieldAriaLabel={"Enter address of the property for this loan"}
              searchOptionsTypes={[]}
              externalLocalAddress={address !== "" && address}
              error={answer.a_error}
            />
          </Box>
        );
        break;
      case "date":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <>
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["month", "year"]}
                    label={"Date"}
                    value={
                      currentValues[answer.q_title] === undefined
                        ? null
                        : dayjs(currentValues[answer.q_title])
                    }
                    onChange={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM")
                        : "";

                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = newDateString;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        newDateString,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onBlur={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM") // it needs this input to read in // but we like to display mm / yyyy in freedomlandia
                        : "";
                      processSubmission(newDateString);
                    }}
                    format="MM/YYYY"
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Box>
            </>
          </Box>
        );
        break;
      default:
    }

    return answerOptionsUI;
  };

  const resetCalculator = () => {
    // Loop through all answers and set a_string to ""
    const updatedAnswers = answers.map((answer) => ({
      ...answer,
      a_string: "",
    }));

    // Reset current values to ""
    const updatedCurrentValues = Object.keys(currentValues).reduce(
      (acc, key) => {
        acc[key] = ""; // Set each current value to an empty string
        return acc;
      },
      {}
    );

    setCurrentValues(updatedCurrentValues); // Update current values in state
  };

  return (
    <Box
      key={`calculation_${externalCalc?.id ? externalCalc.id : 0}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: theme.spacing(0, "auto"),
        width: "100%",
      }}
    >
      {router.pathname.includes("/my/calculation") && calc && (
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            sx={{
              margin: theme.spacing("auto", 0.5, "auto", "auto"),
            }}
            key={`icon_button_delete_calc_${calc.id}`}
            aria-label="loan settings"
          >
            <MoreVertIcon
              key={`vert_icon_delete_calc_${calc.id}`}
              aria-controls={
                Boolean(anchorEl[calc.id]) ? "basic-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl[calc.id]) ? "true" : undefined}
              onClick={(e) => handleOpen(e, calc)}
            />
            <Menu
              key={`menu_delete_calc_${calc.id}`}
              anchorEl={anchorEl[calc.id]}
              open={Boolean(anchorEl[calc.id])}
              onClose={() => handleClose(calc)}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem key={`menu_item_delete_${calc.id}`}>
                <Button
                  sx={{ width: "100%" }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    (async () => {
                      await updateCalc(user, calc, {
                        Deleted: true,
                      });

                      handleClose(calc);

                      await sleep(1000); // BE deleted flag need a sec to return on next call
                      await loadCalcs(refCalcs);
                    })();
                  }}
                >
                  Delete calculation
                </Button>
              </MenuItem>
            </Menu>
          </IconButton>
        </Box>
      )}
      <Button
        variant="outlined"
        color="secondary"
        onClick={resetCalculator}
        sx={{
          margin: theme.spacing(1, 1, 1, "auto"),
          width: "25%",
          [theme.breakpoints.down("md")]: {
            width: "33%",
            margin: theme.spacing(1, "auto", 1, 1),
          },
          "@media print": { display: "none" }, // Hide when printing
        }}
        className="no-print-element" // Add a class to identify it for PDF generation
      >
        Reset
      </Button>

      {answers.length > 0 ? (
        <Box id="calculator-to-print">
          <Box id="pdf-1-input">
            <QuestionRenderer
              answers={answers}
              questions={firstPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
            />
          </Box>

          <Box
            id="pdf-2-total-loan"
            sx={{
              margin: theme.spacing(1, 0, 0, 0),
              padding: theme.spacing(1, 3),
              borderRadius: theme.spacing(1),
              border: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow: `0px 2px 4px ${theme.palette.grey[200]}`,
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <Box
              sx={{
                margin: theme.spacing(1, 0, 0, 0),
                [theme.breakpoints.down("md")]: {
                  margin: theme.spacing(0, 0, 0, 0),
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <ButtonBase
                      sx={{
                        width: "100%",
                        padding: theme.spacing(1),
                        "&:focus": {
                          outline: "none",
                          borderRadius: theme.spacing(0.5),
                          boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                            theme.palette.info.main
                          }`,
                        },
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1),
                          padding: 0,
                        },
                      }}
                      onClick={() => {
                        let updatedShowQuestionDescription = Object.assign(
                          {},
                          showQuestionDescription
                        );

                        updatedShowQuestionDescription[`Monthly cashflow`] =
                          !updatedShowQuestionDescription[`Monthly cashflow`];

                        setShowQuestionDescription(
                          updatedShowQuestionDescription
                        );
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: theme.spacing(3),
                          textAlign: "left",
                          [theme.breakpoints.down("md")]: {
                            fontSize: theme.spacing(2),
                          },
                        }}
                      >
                        Monthly cashflow
                      </Typography>
                      {showQuestionDescription[`Monthly cashflow`] ? (
                        <KeyboardArrowUpIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                        />
                      ) : (
                        <InfoOutlinedIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                          color="info"
                        />
                      )}
                    </ButtonBase>
                  </Box>
                  <Box
                    sx={{
                      fontWeight: 600,
                      fontSize: theme.spacing(2.5),
                      textAlign: "center",
                      flex: "0 1 50%",
                      margin: "auto",
                      color:
                        currentValues["Monthly cashflow"] ||
                        monthlyCashflow >= 0
                          ? theme.palette.success.main
                          : theme.palette.secondary.main,
                    }}
                  >
                    {`$${formatMoney(
                      parseFloat(
                        currentValues["Monthly cashflow"] ||
                          monthlyCashflow ||
                          0
                      ),
                      0
                    )}/month`}
                  </Box>
                </Box>
                <Collapse in={showQuestionDescription[`Monthly cashflow`]}>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        margin: theme.spacing(1),
                        fontSize: theme.spacing(1.8),
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1, 0, 1, 2),
                        },
                      }}
                    >
                      {`$${formatMoney(
                        parseFloat(currentValues["Monthly rent"] || 0),
                        0
                      )}`}{" "}
                      Monthly rent
                      <br />-{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Monthly mortgage"] || 0),
                        0
                      )}`}{" "}
                      Monthly mortgage
                      <br />-{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Annual taxes"] / 12 || 0),
                        0
                      )}`}{" "}
                      Monthly taxes
                      <br />-{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Annual insurance"] / 12 || 0),
                        0
                      )}`}{" "}
                      Monthly insurance
                      <br />-{" "}
                      {`$${formatMoney(
                        parseFloat(currentValues["Monthly reserve"] || 0),
                        0
                      )}`}{" "}
                      Monthly reserve
                      <br />={" "}
                      {!userProfile || Object.keys(userProfile).length === 0
                        ? `$0.00`
                        : `$${formatMoney(
                            parseFloat(
                              currentValues["Monthly cashflow"] ||
                                monthlyCashflow ||
                                0
                            ),
                            0
                          )}`}{" "}
                      Monthly cashflow
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </Box>
            <Box
              sx={{
                margin: theme.spacing(1.5, 0),
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <ButtonBase
                      sx={{
                        width: "100%",
                        padding: theme.spacing(1),
                        "&:focus": {
                          outline: "none",
                          borderRadius: theme.spacing(0.5),
                          boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                            theme.palette.info.main
                          }`,
                        },
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1),
                          padding: 0,
                        },
                      }}
                      onClick={() => {
                        let updatedShowQuestionDescription = Object.assign(
                          {},
                          showQuestionDescription
                        );

                        updatedShowQuestionDescription[`Annual cashflow`] =
                          !updatedShowQuestionDescription[`Annual cashflow`];

                        setShowQuestionDescription(
                          updatedShowQuestionDescription
                        );
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: theme.spacing(3),
                          textAlign: "left",
                          [theme.breakpoints.down("md")]: {
                            fontSize: theme.spacing(2),
                          },
                        }}
                      >
                        Annual cashflow
                      </Typography>
                      {showQuestionDescription[`Annual cashflow`] ? (
                        <KeyboardArrowUpIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                        />
                      ) : (
                        <InfoOutlinedIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                          color="info"
                        />
                      )}
                    </ButtonBase>
                  </Box>
                  <Box
                    sx={{
                      fontWeight: 600,
                      fontSize: theme.spacing(2.5),
                      textAlign: "center",
                      flex: "0 1 50%",
                      margin: "auto",
                      color:
                        annualCashflow >= 0
                          ? theme.palette.success.main
                          : theme.palette.secondary.main,
                    }}
                  >
                    ${formatMoney(annualCashflow, 0)}
                  </Box>
                </Box>
                <Collapse in={showQuestionDescription[`Annual cashflow`]}>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        margin: theme.spacing(1),
                        fontSize: theme.spacing(1.8),
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1, 0, 1, 2),
                        },
                      }}
                    >
                      ${formatMoney(monthlyCashflow, 0)} Monthly cashflow
                      <br />* 12 Months
                      <br />={" "}
                      {`$${formatMoney(
                        parseFloat(
                          currentValues["annual Cashflow"] ||
                            annualCashflow ||
                            0
                        ),
                        0
                      )}`}{" "}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Box>

          <Box id="pdf-3-pmts-2">
            <QuestionRenderer
              answers={answers}
              questions={secondPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
            />
          </Box>
          <Box
            id="pdf-4-total-pmt-cash-to-close"
            sx={{
              margin: theme.spacing(1, 0, 0, 0),
              padding: theme.spacing(1, 3),
              borderRadius: theme.spacing(1),
              border: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow: `0px 2px 4px ${theme.palette.grey[200]}`,
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <Box
              sx={{
                margin: theme.spacing(1, 0, 0, 0),
                padding: theme.spacing(1, 3),
                borderRadius: theme.spacing(1),

                [theme.breakpoints.down("md")]: {
                  padding: theme.spacing(1),
                },
              }}
            >
              <Box
                sx={{
                  margin: theme.spacing(1, 0, 0, 0),
                  [theme.breakpoints.down("md")]: {
                    margin: theme.spacing(0, 0, 0, 0),
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: theme.spacing(1),
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          let updatedShowQuestionDescription = Object.assign(
                            {},
                            showQuestionDescription
                          );

                          updatedShowQuestionDescription[`Invested cash`] =
                            !updatedShowQuestionDescription[`Invested cash`];

                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontSize: theme.spacing(3),
                            textAlign: "left",
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(2),
                            },
                          }}
                        >
                          Invested cash
                        </Typography>
                        {showQuestionDescription[`Invested cash`] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>
                    <Box
                      sx={{
                        fontWeight: 600,
                        fontSize: theme.spacing(2.5),
                        textAlign: "center",
                        flex: "0 1 50%",
                        margin: "auto",
                      }}
                    >
                      ${formatMoney(totalInvestment, 0)}{" "}
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription[`Invested cash`]}>
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          margin: theme.spacing(1),
                          fontSize: theme.spacing(1.8),
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1, 0, 1, 1),
                          },
                        }}
                      >
                        {`$${formatMoney(
                          parseFloat(currentValues["Downpayment"] || 0),
                          0
                        )}`}{" "}
                        Downpayment
                        <br />+{" "}
                        {`$${formatMoney(
                          parseFloat(currentValues["Closing costs"] || 0),
                          0
                        )}`}{" "}
                        Closing costs
                        <br />={" "}
                        {`$${formatMoney(
                          parseFloat(
                            currentValues["totalInvestment"] ||
                              totalInvestment ||
                              0
                          ),
                          0
                        )}`}{" "}
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                margin: theme.spacing(1, 0, 0, 0),
                padding: theme.spacing(1, 3),
                borderRadius: theme.spacing(1),
                [theme.breakpoints.down("md")]: {
                  padding: theme.spacing(1),
                },
              }}
            >
              <Box
                sx={{
                  margin: theme.spacing(1, 0, 0, 0),
                  [theme.breakpoints.down("md")]: {
                    margin: theme.spacing(0, 0, 0, 0),
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: theme.spacing(1),
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          let updatedShowQuestionDescription = Object.assign(
                            {},
                            showQuestionDescription
                          );
                          updatedShowQuestionDescription[
                            `Cash on cash return`
                          ] =
                            !updatedShowQuestionDescription[
                              `Cash on cash return`
                            ];

                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontSize: theme.spacing(4),
                            textAlign: "left",
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(2),
                            },
                          }}
                        >
                          Cash on cash return
                        </Typography>
                        {showQuestionDescription[`Cash on cash return`] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: theme.spacing(2),
                        [theme.breakpoints.down("md")]: {
                          flexDirection: "column",
                          gap: theme.spacing(1),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          fontWeight: 600,
                          fontSize: theme.spacing(4),
                          textAlign: "center",
                        }}
                      >
                        {(cashOnCashReturn * 100).toFixed(2)}%
                      </Box>
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription[`Cash on cash return`]}>
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          margin: theme.spacing(1),
                          fontSize: theme.spacing(1.8),
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1, 0, 1, 2),
                          },
                        }}
                      >
                        {`$${formatMoney(parseFloat(annualCashflow || 0), 0)}`}{" "}
                        Annual cashflow
                        <br />/ ${formatMoney(totalInvestment, 0)} Invested cash
                        <br />= {`${(cashOnCashReturn * 100).toFixed(2)}%`} Cash
                        on cash return
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* <Box id="pdf-5-fees">
            <QuestionRenderer
              answers={answers}
              questions={thirdPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
              calculatorType={calcType}
            />
          </Box> */}

          {/* TODO */}
          <Box id="pdf-5-save-share-pdf-questions">
            <SaveSharePDF
              answers={answers}
              allowedQuestionTitles={thirdPart}
              generateAnswerOptions={generateAnswerOptions}
              calc={calc}
              setCalc={setCalc}
              calculatorType={calcType}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{ margin: theme.spacing(2, 0), width: "100%", minHeigh: "100vh" }}
        >
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
        </Box>
      )}
      <Dialog
        open={showLogInDialog}
        onClose={() => setShowLogInDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Authenticator customTitle={"Log in or sign up to do that"} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
